import { createElement, forwardRef } from 'react';
import { Icon } from '../Icon';

import { BaseHelpPopup } from '../HelpPopup/__internal__/';
import { useDesignTokens, DesignTokens, renderDesignTokens } from '../../theme';
import { useTruncationTooltip } from '../../hooks/useTruncationTooltip';
import { postfixTestId } from '../../util/testId';

import { LabelProps, LabelVariant, TokensProp } from '.';
import { useHighlightableText } from '../../hooks/useHighlightableText';

import cx from 'classnames';
import cn from './Label.css.json';
import './Label.css';

export const Label = forwardRef<HTMLLabelElement, LabelProps>((props: LabelProps, ref) => {
  const dt = useDesignTokens();

  const primaryLight = dt.themeVariant === 'light' && props.variant === LabelVariant.PrimaryLight;
  const children = useHighlightableText({
    text: props.children ?? '',
    className: cn.highlight,
    ...props.highlightableConfig,
  });

  const setRef = useTruncationTooltip(ref);

  const tokens = {
    ...getLabelDesignTokens(dt, { disabled: props.disabled, primaryLight, tokens: props.tokens }),
    ...props.theme?.(dt),
  };

  const requiredSymbol = '*';

  return (
    <div
      className={cx(cn.label, props.className, props.wrapText === false && cn.nowrap)}
      style={{ ...tokens, ...props.style }}
      data-testid={props.testId}
      ref={props.containerRef}
    >
      {props.icon ? (
        <Icon
          {...props.icon}
          className={cx(props.icon.className, cn.icon)}
          disabled={props.disabled}
          tokens={() =>
            props.variant === LabelVariant.PrimaryLight
              ? {
                  fg:
                    props.tokens?.().fg ??
                    (primaryLight ? dt.c.colorTextInverse : dt.c.colorTextLabel),
                }
              : {}
          }
        />
      ) : null}
      <span className={cn.textContainer}>
        <label
          htmlFor={props.htmlFor}
          className={cn.text}
          ref={props.wrapText === false && !props.suppressTooltip ? setRef : ref}
          id={props.id}
          aria-disabled={props.disabled}
        >
          {children}
        </label>
        {props.required && (
          <span className={cn.requiredMark} aria-hidden>
            {requiredSymbol}
          </span>
        )}
        {props.helpPopup ? (
          <BaseHelpPopup
            testId={postfixTestId(props.testId, 'helpPopup')}
            {...props.helpPopup}
            className={cx(props.helpPopup.className, cn.help)}
            additionalAriaText={props.children}
          />
        ) : null}
      </span>
    </div>
  );
});

export const getLabelDesignTokens = (
  dt: DesignTokens,
  opts?: { disabled?: boolean; primaryLight?: boolean; tokens?: TokensProp }
) => {
  let labelFG =
    opts?.tokens?.().fg ?? (opts?.primaryLight ? dt.c.colorTextInverse : dt.c.colorTextLabel);
  labelFG = opts?.disabled
    ? opts?.primaryLight
      ? dt.statePrimaryNova.disabled.fg
      : dt.stateStandardNova.disabled.fg
    : labelFG;

  return {
    ...renderDesignTokens(
      {
        fg: labelFG,
        'asterisk-fg': opts?.disabled ? labelFG : dt.c.colorAlert,
        'font-size-m': dt.fontNova.size.m,
        'highlighted-bg': dt.text.highlighted.bg,
        'highlighted-fg': dt.text.highlighted.fg,
        'text-cursor': 'text',
      },
      'label'
    ),
    ...renderDesignTokens(
      {
        'squish-xxs': dt.spacing.insetSquish.xxs.padding,
      },
      'help-icon-spacing'
    ),
  };
};
